import React, { useState, useEffect, useCallback } from 'react';
import { HiOutlineServer, HiOutlineShieldCheck, HiOutlineClock, HiOutlineRefresh } from 'react-icons/hi';
import { SystemSkeleton } from '../components/Skeleton';
import ConfirmModal from '../components/ConfirmModal';

const API_BASE = process.env.REACT_APP_API_URL || '';
const REFRESH_INTERVAL = 15000;

function getProgressColor(percent) {
  if (percent < 60) return 'green';
  if (percent < 85) return 'yellow';
  return 'red';
}

export default function SystemPage({ token }) {
  const [system, setSystem] = useState(null);
  const [mysql, setMySQL] = useState(null);
  const [ssl, setSSL] = useState(null);
  const [sslRenewStatus, setSSLRenewStatus] = useState(null);
  const [slowQueries, setSlowQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [sysRes, mysqlRes, sslRes, sslRenewRes, sqRes] = await Promise.all([
        fetch(`${API_BASE}/api/monitoring/system`, { headers }),
        fetch(`${API_BASE}/api/monitoring/mysql`, { headers }),
        fetch(`${API_BASE}/api/monitoring/ssl`, { headers }),
        fetch(`${API_BASE}/api/monitoring/ssl/renew-status`, { headers }).catch(() => null),
        fetch(`${API_BASE}/api/monitoring/mysql/slow-queries`, { headers }),
      ]);
      if (sysRes.ok) setSystem(await sysRes.json());
      if (mysqlRes.ok) setMySQL(await mysqlRes.json());
      if (sslRes.ok) setSSL(await sslRes.json());
      if (sslRenewRes?.ok) setSSLRenewStatus(await sslRenewRes.json()); else setSSLRenewStatus(null);
      if (sqRes.ok) {
        const sqData = await sqRes.json();
        setSlowQueries(sqData.queries || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const doAction = async (endpoint, body = {}) => {
    setActionLoading(endpoint);
    setActionMessage(null);
    try {
      const res = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok) {
        setActionMessage({ type: 'success', text: data.message || 'OK' });
        fetchData();
      } else {
        setActionMessage({ type: 'error', text: data.error || `Erreur ${res.status}` });
      }
    } catch (err) {
      setActionMessage({ type: 'error', text: err.message });
    } finally {
      setActionLoading(null);
      setTimeout(() => setActionMessage(null), 8000);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (loading) {
    return <SystemSkeleton />;
  }

  return (
    <div className="fade-in">
      {/* System Metrics */}
      <div className="grid-3" style={{ marginBottom: 24 }}>
        {/* CPU */}
        <div className="card">
          <div className="card-title" style={{ marginBottom: 16 }}>CPU Load</div>
          <div className="metric-value">{system?.cpu?.load1?.toFixed(2)}</div>
          <div className="metric-label">Moyenne 1 min</div>
          <div className="progress-bar" style={{ marginTop: 12 }}>
            <div
              className={`progress-fill ${getProgressColor(Math.min(system?.cpu?.load1 * 50, 100))}`}
              style={{ width: `${Math.min(system?.cpu?.load1 * 50, 100)}%` }}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, fontSize: 13, color: 'var(--text-muted)' }}>
            <span>5m: {system?.cpu?.load5?.toFixed(2)}</span>
            <span>15m: {system?.cpu?.load15?.toFixed(2)}</span>
          </div>
        </div>

        {/* Memory */}
        <div className="card">
          <div className="card-title" style={{ marginBottom: 16 }}>Mémoire</div>
          <div className="metric-value">{system?.memory?.percent}%</div>
          <div className="metric-label">{system?.memory?.usedMB} / {system?.memory?.totalMB} Mo</div>
          <div className="progress-bar" style={{ marginTop: 12 }}>
            <div
              className={`progress-fill ${getProgressColor(system?.memory?.percent)}`}
              style={{ width: `${system?.memory?.percent}%` }}
            />
          </div>
          <div style={{ marginTop: 12, fontSize: 13, color: 'var(--text-muted)' }}>
            Disponible : {system?.memory?.availMB} Mo
          </div>
        </div>

        {/* Disk */}
        <div className="card">
          <div className="card-title" style={{ marginBottom: 16 }}>Disque</div>
          <div className="metric-value">{system?.disk?.percent}%</div>
          <div className="metric-label">{Math.round(system?.disk?.usedMB / 1024)} / {Math.round(system?.disk?.totalMB / 1024)} Go</div>
          <div className="progress-bar" style={{ marginTop: 12 }}>
            <div
              className={`progress-fill ${getProgressColor(system?.disk?.percent)}`}
              style={{ width: `${system?.disk?.percent}%` }}
            />
          </div>
          <div style={{ marginTop: 12, fontSize: 13, color: 'var(--text-muted)' }}>
            Libre : {Math.round(system?.disk?.availMB / 1024)} Go
          </div>
        </div>
      </div>

      {/* MySQL + SSL */}
      <div className="grid-2" style={{ marginBottom: 24 }}>
        {/* MySQL */}
        <div className="card">
          <div className="card-header">
            <div className="card-title"><HiOutlineServer className="icon" /> MySQL</div>
            <span className={`badge ${mysql?.connected ? 'healthy' : 'down'}`}>
              {mysql?.connected ? 'CONNECTÉ' : 'DOWN'}
            </span>
          </div>
          {mysql?.connected && (
            <table>
              <tbody>
                <tr><td className="metric-label">Taille DB</td><td style={{ fontWeight: 600 }}>{(mysql.dbSizeMB / 1024).toFixed(2)} Go</td></tr>
                <tr><td className="metric-label">Threads actifs</td><td style={{ fontWeight: 600 }}>{mysql.threadsConnected}</td></tr>
                <tr><td className="metric-label">Processus actifs</td><td style={{ fontWeight: 600 }}>{mysql.activeProcesses}</td></tr>
                <tr><td className="metric-label">Requêtes totales</td><td style={{ fontWeight: 600 }}>{mysql.totalQueries?.toLocaleString()}</td></tr>
                <tr><td className="metric-label">Requêtes lentes</td><td style={{ fontWeight: 600, color: mysql.slowQueries > 0 ? 'var(--accent-yellow)' : 'inherit' }}>{mysql.slowQueries}</td></tr>
                <tr><td className="metric-label">Uptime</td><td style={{ fontWeight: 600 }}>{Math.floor(mysql.uptime / 86400)}j</td></tr>
              </tbody>
            </table>
          )}
        </div>

        {/* SSL Certificates */}
        <div className="card">
          <div className="card-header">
            <div className="card-title"><HiOutlineShieldCheck className="icon" /> Certificats SSL</div>
            <button
              className="btn btn-outline"
              style={{ padding: '4px 10px', fontSize: 12 }}
              disabled={!!actionLoading}
              onClick={() => setConfirmModal({
                title: 'Renouveler les certificats SSL',
                message: 'Lance `certbot renew` sur le serveur production. Les certificats dont l\'expiration est < 30 jours seront renouvelés.',
                confirmLabel: 'Renouveler',
                onConfirm: () => { setConfirmModal(null); doAction('/api/monitoring/ssl/renew'); },
              })}
            >
              {actionLoading === '/api/monitoring/ssl/renew' ? <span className="spin">⟳</span> : <HiOutlineRefresh />}
              Renouveler
            </button>
          </div>

          {/* Last renew attempt info */}
          {sslRenewStatus?.available && sslRenewStatus.timestamp && (
            <div style={{
              padding: '8px 12px',
              marginBottom: 8,
              borderRadius: 6,
              background: 'var(--bg-tertiary)',
              fontSize: 12,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <span className="metric-label">
                Dernier renew : <strong style={{ color: 'var(--text-primary)' }}>{new Date(sslRenewStatus.timestamp).toLocaleString('fr-FR')}</strong>
                {sslRenewStatus.trigger && <> · <em>{sslRenewStatus.trigger}</em></>}
              </span>
              <span className={`badge ${sslRenewStatus.success ? 'healthy' : 'down'}`}>
                {sslRenewStatus.success ? '✓ OK' : `✗ exit=${sslRenewStatus.exitCode}`}
              </span>
            </div>
          )}
          {sslRenewStatus && !sslRenewStatus.available && (
            <div style={{ fontSize: 12, color: 'var(--accent-yellow)', marginBottom: 8 }}>
              ⚠ Auto-renew non configuré ({sslRenewStatus.message || 'migration 002 requise'})
            </div>
          )}

          {actionMessage && (
            <div style={{
              padding: '8px 12px',
              marginBottom: 8,
              borderRadius: 6,
              background: actionMessage.type === 'success' ? 'rgba(34,197,94,.15)' : 'rgba(239,68,68,.15)',
              color: actionMessage.type === 'success' ? 'var(--accent-green)' : 'var(--accent-red)',
              fontSize: 12,
            }}>
              {actionMessage.text}
            </div>
          )}

          {ssl?.certificates?.length ? ssl.certificates.map(cert => (
            <div key={cert.name} style={{ padding: '12px 0', borderBottom: '1px solid var(--border-color)' }}>
              <div style={{ fontWeight: 500, marginBottom: 4 }}>{cert.name}</div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 4 }}>{cert.domains}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span className="metric-label">Expire : {cert.expiryDate}</span>
                <span className={`badge ${cert.daysRemaining > 30 ? 'healthy' : cert.daysRemaining > 7 ? 'degraded' : 'down'}`}>
                  {cert.daysRemaining}j
                </span>
              </div>
            </div>
          )) : (
            <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '12px 0' }}>
              Aucun certificat détecté
            </div>
          )}
        </div>
      </div>

      {/* Slow Queries */}
      {slowQueries.length > 0 && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-header">
            <div className="card-title"><HiOutlineClock className="icon" /> Top requêtes lentes</div>
            <span className="badge degraded">{slowQueries.length} requêtes</span>
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Requête</th>
                  <th>Exécutions</th>
                  <th>Moy. (ms)</th>
                  <th>Max (ms)</th>
                  <th>Total (ms)</th>
                </tr>
              </thead>
              <tbody>
                {slowQueries.map((q, i) => (
                  <tr key={i}>
                    <td>
                      <div className="query-text" title={q.query}>
                        {q.query?.substring(0, 80)}{q.query?.length > 80 ? '…' : ''}
                      </div>
                    </td>
                    <td>{q.exec_count?.toLocaleString()}</td>
                    <td style={{ color: q.avg_time_ms > 100 ? 'var(--accent-yellow)' : 'inherit' }}>{q.avg_time_ms}</td>
                    <td style={{ color: q.max_time_ms > 1000 ? 'var(--accent-red)' : 'inherit' }}>{q.max_time_ms}</td>
                    <td>{q.total_time_ms?.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Server info */}
      <div className="card">
        <div className="card-title" style={{ marginBottom: 12 }}>Informations serveur</div>
        <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', fontSize: 14 }}>
          <div><span className="metric-label">Uptime :</span> <strong>{system?.uptime?.formatted}</strong></div>
          <div><span className="metric-label">Instance :</span> <strong>m6i.large</strong></div>
          <div><span className="metric-label">IP :</span> <strong>18.135.121.189</strong></div>
          <div><span className="metric-label">Région :</span> <strong>eu-west-2</strong></div>
        </div>
      </div>

      {/* Confirm modal */}
      <ConfirmModal
        open={!!confirmModal}
        title={confirmModal?.title}
        message={confirmModal?.message}
        warning={confirmModal?.warning}
        confirmLabel={confirmModal?.confirmLabel}
        danger={confirmModal?.danger}
        onConfirm={confirmModal?.onConfirm}
        onCancel={() => setConfirmModal(null)}
      />
    </div>
  );
}
